import { DateTime } from 'luxon';

/**
 * Parses a hospitalization event date into a local start-of-day.
 * @param hospitalizationEventDate Date in `yyyy-MM-dd` or ISO-8601 format.
 * @returns Parsed day or null when the value is missing or invalid.
 */
function parseHospitalizationEventDay(
  hospitalizationEventDate: string
): DateTime | null {
  const trimmed = hospitalizationEventDate.trim();
  if (!trimmed) {
    return null;
  }

  const fromDateOnly = DateTime.fromFormat(trimmed, 'yyyy-MM-dd');
  if (fromDateOnly.isValid) {
    return fromDateOnly.startOf('day');
  }

  const yyyyMmDdPrefix = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed);
  if (yyyyMmDdPrefix) {
    const fromPrefix = DateTime.fromFormat(yyyyMmDdPrefix[1], 'yyyy-MM-dd');
    if (fromPrefix.isValid) {
      return fromPrefix.startOf('day');
    }
  }

  const fromIso = DateTime.fromISO(trimmed);
  if (fromIso.isValid) {
    return fromIso.startOf('day');
  }

  return null;
}

/**
 * Returns whether an interaction occurred on or after a hospitalization event day.
 * @param hospitalizationEventDate Admit/discharge date in `yyyy-MM-dd` format.
 * @param interactionTimestampMs Interaction time in epoch milliseconds.
 * @returns True when the hospitalization day is on or before the interaction day.
 */
export function isInteractionEligibleForHospitalizationFollowUp(
  hospitalizationEventDate: string | null | undefined,
  interactionTimestampMs: number
): boolean {
  if (!hospitalizationEventDate) {
    return false;
  }

  const hospitalizationDay = parseHospitalizationEventDay(
    hospitalizationEventDate
  );

  if (!hospitalizationDay) {
    return false;
  }

  const interactionDay = DateTime.fromMillis(interactionTimestampMs).startOf(
    'day'
  );

  if (!interactionDay.isValid) {
    return false;
  }

  return hospitalizationDay <= interactionDay;
}
